import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RouterState, push } from 'connected-react-router';

import { FetchState, FetchStatus } from 'common/modules/app/fetch';
import { GlossaryState } from 'common/modules/app/glossaries';
import { Principal } from 'common/modules/app/principal';

import {
  PurchaseInformation,
  getPurchasesHistory,
  getPurchaseHistoryWithin6Months,
  reset,
  StateType,
  SortType,
  GET_PURCHASE_HISTORY_ID,
  PurchaseInitialState
} from 'common/modules/domain/purchase';
import { UserInformation, getLogin } from 'common/modules/domain/user';

import { loadCookie, SasCookieKeys } from 'common/utils/cookie-utils';
import { Glossary } from 'common/utils/glossary';
import { SasPagePath, createQuery } from 'common/utils/urls';
import { ExpirationType, checkExpirationDatetime } from 'common/utils/date';

import { AppState } from 'pages';
import { PurchaseHistoryLists } from 'pages/purchase/components/PurchaseHistoryLists';
import { PlanStatus } from 'pages/purchase/components/PurchaseHistory';

/**
 * 売り切りプランの状態判別処理
 * @param p 購入情報
 * @param now 現在時刻
 * @returns data={ plan_status:プランの状態, is_show:再購入ボタン表示有無 }
 */
const __determinePlanStatusForSellout = (
  p: PurchaseInformation,
  now: number
) => {
  // 売り切りプランの状態を判別
  // p.stateは"contract"：契約中 or "cancel"：解約
  let status = PlanStatus.CONTRACT;
  let is_show = false;
  if (p.state == StateType.TYPE_CANCEL) {
    // 解約の場合
    status = PlanStatus.CANCEL;
    is_show = true;
    // 有効期限がある場合は有効期限切れか否かを確認する
    if (p.expirationDatetime != null) {
      // 売り切りプランの場合は猶予期間は0としてチェックする
      const expiration = checkExpirationDatetime(now, p.expirationDatetime, 0);
      if (expiration == ExpirationType.TYPE_EXPIRED) {
        // 有効期限切れの場合
        status = PlanStatus.EXPIRED;
      }
    }
  }
  return {
    plan_status: status,
    is_show: is_show
  };
};

/**
 * サブスクリプションプランの状態判別処理
 * @param p 購入情報
 * @param now 現在時刻
 * @returns data={ plan_status:プランの状態, is_show:再購入ボタン表示有無 }
 */
const __determinePlanStatusForSubscription = (
  p: PurchaseInformation,
  now: number
) => {
  // サブスクリプションプランプランの状態を判別
  // p.stateは"contract"：契約中 or "cancel"：解約 or "individual_cancel" : 個別解約
  let status = PlanStatus.CONTRACT;
  let is_show = false;
  if (p.renewalType == 'Manual') {
    // 自動更新OFFの場合
    if (p.state == StateType.TYPE_CANCEL) {
      // 解約の場合
      status = PlanStatus.CANCEL;
      is_show = true;
    } else {
      // 契約中or個別解約の場合
      status = PlanStatus.MANUAL_UPDATE;
      if (p.state == StateType.TYPE_CONTRACT) {
        // 契約中の場合は有効期限をチェック
        const expiration = checkExpirationDatetime(
          now,
          p.expirationDatetime,
          p.expirationGraceDays
        );
        if (expiration == ExpirationType.TYPE_EXPIRED) {
          // 有効期限切れの場合
          status = PlanStatus.HOLD;
        }
      } else {
        // 個別解約の場合は「再購入」ボタンを表示
        is_show = true;
      }
    }
  } else {
    // 自動更新ONの場合
    if (p.state == StateType.TYPE_CANCEL) {
      // 解約の場合
      status = PlanStatus.CANCEL;
      is_show = true;
    } else if (p.state == StateType.TYPE_INDIVIDUAL_CANCEL) {
      // 個別解約の場合
      status = PlanStatus.INDIVIDUAL_CANCEL;
      is_show = true;
    } else {
      // 契約中の場合は有効期限をチェック
      const expiration = checkExpirationDatetime(
        now,
        p.expirationDatetime,
        p.expirationGraceDays
      );
      if (expiration == ExpirationType.TYPE_EXPIRED) {
        // 有効期限切れの場合
        status = PlanStatus.HOLD;
      }
    }
  }
  return {
    plan_status: status,
    is_show: is_show
  };
};

/** PlanをStateからPropsに変換する */
const _convertPlans = (
  purchaseList: PurchaseInformation[],
  language: string,
  onClickPurchase: (planId: string) => () => void,
  onClickDetail: (planId: string) => () => void
) => {
  // 現在時刻をループ前に取得
  const now = Date.now();
  const _purchaseHistorys = [];
  for (const p of purchaseList) {
    // operationDatetimeを表示用の形式に変換
    const operationTime = new Intl.DateTimeFormat(`${language}`).format(
      Date.parse(p.operationDatetime)
    );
    // プランの状態を判別
    let data;
    // 売り切り,サブスクリプションの判別
    if (
      p.expirationDatetime != null &&
      p.expirationGraceDays != null &&
      p.updateDatetime != null
    ) {
      // サブスクリプションプランの場合
      data = __determinePlanStatusForSubscription(p, now);
    } else {
      // 売り切りプランの場合
      data = __determinePlanStatusForSellout(p, now);
    }
    _purchaseHistorys.push({
      vendorPurchaseId: p.vendorPurchaseId,
      planName: p.plan.name,
      operationDatetime: operationTime,
      status: data.plan_status,
      isShow: data.is_show,
      onClickPurchase: onClickPurchase(p.plan.planId),
      onClickDetail: onClickDetail(p.plan.planId)
    });
  }

  return _purchaseHistorys;
};

/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const purchaseState = useSelector<AppState, PurchaseInitialState>(
    (state) => state.domain.purchase
  );
  const userState = useSelector<AppState, UserInformation>(
    (state) => state.domain.user
  );
  const principalState = useSelector<AppState, Principal>(
    (state) => state.app.principal
  );
  const routerState = useSelector<AppState, RouterState>(
    (state) => state.app.router
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const fetchState = useSelector<AppState, FetchState>(
    (state) => state.app.fetch
  );
  const isPurchaseProcessing =
    fetchState[GET_PURCHASE_HISTORY_ID] !== null &&
    fetchState[GET_PURCHASE_HISTORY_ID] === FetchStatus.PROCESSING;
  // 契約中プラン一覧側で取得開始している可能性があるので、処理中の場合は何もしない
  const isPurchaseFetched =
    fetchState[GET_PURCHASE_HISTORY_ID] !== null &&
    (fetchState[GET_PURCHASE_HISTORY_ID] === FetchStatus.UNTREATED ||
      fetchState[GET_PURCHASE_HISTORY_ID] === FetchStatus.PROCESSING);

  return {
    purchase: purchaseState,
    principal: principalState,
    user: userState,
    router: routerState,
    glossary: Glossary.instance(glState),
    isFetched: isPurchaseFetched,
    isProcessing: isPurchaseProcessing
  };
};

type SelectedState = ReturnType<typeof useStateProps>;
/**
 * StoreのStateとActionをdispatchする関数からComponentに渡すPropsへ
 */
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();
  const cookie = loadCookie();
  const _token = cookie[SasCookieKeys.KEY_TOKEN];
  const isLoggedIn = state.principal.isLoggedIn;
  const _language = state.user.language;
  const _from = '';
  // Stateから6か月以内のデータを取得
  const _data = getPurchaseHistoryWithin6Months(
    state.purchase.initialPurchasePlanList,
    false
  );

  /** 購入履歴一覧画面の初期化処理 */
  const onInit = async () => {
    // 未ログインだったらログイン画面へ遷移する
    if (!isLoggedIn) {
      dispatch(getLogin());
    } else {
      // 購入履歴情報が未取得のときのみ取得する
      if (!state.isFetched) {
        await dispatch(reset());
        await dispatch(
          getPurchasesHistory(
            _token,
            _language,
            StateType.TYPE_ALL,
            SortType.TYPE_OPERATION_DATETIME,
            _from
          )
        );
      }
    }
  };

  /** 再購入ボタンクリック時の処理 */
  const onClickPurchase = (planId: string) => () => {
    // その場で購入させず、詳細画面へ遷移させて確認してもらう
    const _query = createQuery([{ key: 'planId', value: planId }]);
    dispatch(push(`${SasPagePath.PlanDetail}${_query}`));
  };

  /** プラン名称をクリックした時の処理 */
  const onClickDetail = (planId: string) => () => {
    const _query = createQuery([{ key: 'planId', value: planId }]);
    dispatch(push(`${SasPagePath.PlanDetail}${_query}`));
  };

  return {
    purchaseHistorys: _convertPlans(
      _data,
      _language,
      onClickPurchase,
      onClickDetail
    ),
    initializePage: onInit,
    glossary: state.glossary,
    isLoggedIn: isLoggedIn,
    isProcessing: state.isProcessing
  };
};

/**
 * 購入履歴一覧のContainer Component
 */
export default function ContainerComponent() {
  const _props = { ...useMargeProps(useStateProps()) };
  return <PurchaseHistoryLists {..._props} />;
}
